import { getLocale, t } from '../../i18n';

/** Shared presentation helpers for the client portal pages. */

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return t('N/A');
  return new Date(value).toLocaleString(getLocale(), {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return t('N/A');
  return new Date(value).toLocaleDateString(getLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return t('N/A');
  return new Date(value).toLocaleTimeString(getLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "12 min ago" / "3 h ago" — used for last-seen and clock-in ages. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return t('Never');
  const deltaMs = Date.now() - new Date(value).getTime();
  if (deltaMs < 60_000) return t('Just now');

  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return t('{count} min ago', { count: minutes });

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('{count} h ago', { count: hours });

  return t('{count} d ago', { count: Math.floor(hours / 24) });
}

export function formatDuration(startAt: string | null, endAt: string | null): string {
  if (!startAt) return t('N/A');
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  const minutes = Math.max(0, Math.round((end - new Date(startAt).getTime()) / 60_000));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return t('{count}m', { count: rest });
  return t('{hours}h {minutes}m', { hours, minutes: rest });
}

export function formatLateness(seconds: number): string | null {
  if (!seconds || seconds < 60) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('{count} min late', { count: minutes });
  return t('{count} h late', { count: Math.round(minutes / 60) });
}

/** Site-local clock label for a minute offset that may run past midnight. */
export function formatSlotMinute(minute: number): string {
  const normalised = ((minute % 1440) + 1440) % 1440;
  const hours = Math.floor(normalised / 60).toString().padStart(2, '0');
  const mins = (normalised % 60).toString().padStart(2, '0');
  return `${hours}:${mins}`;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDaysIso(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
